/**
 * Display vocabulary and shared constants for the Regulatory Readiness pages
 * (the heat map at /regulatory and the jurisdiction profile at
 * /regulatory/:jurisdictionCode).
 *
 * Both pages printed the wire codes of regulatory-readiness.openapi.yaml on
 * screen: "ENACTED", "ALT_A", "COMPREHENSIVE", "ENTITY_LEVEL", "HIGH",
 * "2010_AMENDMENTS", and "FCU -- CA" in the institution selector. Each enum map
 * below is keyed by the GENERATED type, so a member added to the spec fails
 * tsc here until it has a label. Lookups go through `enumLabel` in
 * utils/status-labels.tsx, which prints "Unrecognized" for a value outside the
 * map rather than the raw code.
 *
 * article8Variation and article9Revision are free strings in the spec (with
 * defaults UNIFORM and 2010_AMENDMENTS), so their maps hold the values the
 * canonical jurisdiction seed writes.
 *
 * Labels are Title Case, the operator register's casing
 * (.claude/manifests/terminology, context operatorConfig).
 */

import type { components as RegulatoryComponents } from '@aaimplatform/institutional-types/regulatory-readiness';
import { enumLabel } from '../utils/status-labels';

type Schemas = RegulatoryComponents['schemas'];

/**
 * The query parameter carrying the institution profile selected on the heat
 * map. The jurisdiction page reads it to link back to the same selection.
 */
export const REGULATORY_ENTITY_PARAM = 'entity';

/** What a jurisdiction field prints when the profile carries no value for it. */
export const NOT_RECORDED_LABEL = 'Not recorded';

/**
 * The data provenance footer both Regulatory Readiness pages print. The two
 * pages carried two differently worded footers, and the heat map's named
 * internal services, one of which (jurisdiction-rules) does not back this
 * view: both pages read platform-admin-api /api/v1/regulatory/*, which reads
 * the JurisdictionProfile table. The footer names no service. Which service
 * is the arbiter for jurisdiction filing facts is an open operator question.
 */
export const REGULATORY_PROVENANCE_NOTE =
  "Jurisdiction profiles and regulatory rules come from Aaim's regulatory readiness database. This view is read-only; to request additions or corrections, contact";

/** The address the provenance footer links, as a mailto. */
export const REGULATORY_CORRECTIONS_EMAIL = 'compliance@aaim.com';

/**
 * The line under a jurisdiction's name. uccTitleReference is the state code
 * title where the UCC is codified (regulatory-readiness.openapi.yaml,
 * JurisdictionProfile.uccTitleReference); printed bare after the state code
 * it read "CA, Cal. Com. Code", which does not say what the reference is.
 */
export function jurisdictionSubtitle(
  jurisdictionCode: string,
  uccTitleReference: string | null | undefined,
): string {
  if (uccTitleReference === null || uccTitleReference === undefined || uccTitleReference.trim() === '') {
    return `${jurisdictionCode} \u00b7 UCC codification ${NOT_RECORDED_LABEL.toLowerCase()}`;
  }
  return `${jurisdictionCode} \u00b7 UCC codified at ${uccTitleReference}`;
}

export const ARTICLE12_STATUS_LABEL: Record<Schemas['UccArticle12Status'], string> = {
  ENACTED: 'Enacted',
  PENDING: 'Pending',
  NOT_INTRODUCED: 'Not Introduced',
  FAILED: 'Failed',
};

export const DEBTOR_NAMING_RULE_LABEL: Record<Schemas['DebtorNamingRule'], string> = {
  ALT_A: 'Alternative A',
  ALT_B: 'Alternative B',
  NON_UNIFORM: 'Non-Uniform',
};

export const PRIVACY_REGIME_LABEL: Record<Schemas['PrivacyRegime'], string> = {
  COMPREHENSIVE: 'Comprehensive',
  FINANCIAL_ONLY: 'Financial Only',
  NONE: 'None',
};

export const GLBA_EXEMPTION_LABEL: Record<Schemas['GlbaExemptionType'], string> = {
  ENTITY_LEVEL: 'Entity Level',
  INFORMATION_LEVEL: 'Information Level',
  NONE: 'None',
};

export const CONSUMER_PROTECTION_LABEL: Record<Schemas['ConsumerProtectionIntensity'], string> = {
  HIGH: 'High',
  MODERATE: 'Moderate',
  LOW: 'Low',
};

export const LENDER_TYPE_LABEL: Record<Schemas['LenderType'], string> = {
  FCU: 'Federal Credit Union',
  STATE_CU: 'State Credit Union',
  BANK: 'Bank',
  TRUST_COMPANY: 'Trust Company',
  CUSO: 'Credit Union Service Organization',
};

/** Values jurisdiction-seed.ts writes to the free-string article8Variation. */
export const ARTICLE8_VARIATION_LABEL: Record<'UNIFORM' | 'NON_UNIFORM', string> = {
  UNIFORM: 'Uniform',
  NON_UNIFORM: 'Non-Uniform',
};

/** Values jurisdiction-seed.ts writes to the free-string article9Revision. */
export const ARTICLE9_REVISION_LABEL: Record<'2010_AMENDMENTS', string> = {
  '2010_AMENDMENTS': '2010 Amendments',
};

/**
 * A jurisdiction field's label. An absent or empty value prints
 * {@link NOT_RECORDED_LABEL}: the profile did not record it, and no default
 * is substituted for a legal status nobody recorded.
 */
export function jurisdictionLabel<K extends string>(
  map: Record<K, string>,
  value: string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return NOT_RECORDED_LABEL;
  return enumLabel(map, value);
}

/**
 * The selector description for an entity configuration:
 * "Federal Credit Union, IN".
 */
export function entityConfigDescription(
  config: Pick<Schemas['EntityConfiguration'], 'lenderType' | 'lenderCharterState'>,
): string {
  return `${enumLabel(LENDER_TYPE_LABEL, config.lenderType)}, ${config.lenderCharterState}`;
}
